import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { BrokerSymbol } from '@/lib/types';

interface SymbolPickerProps {
  /** O id do campo, para o rótulo apontar para ele. */
  inputId: string;
  value: string;
  onChange: (symbol: string) => void;
  symbols: readonly BrokerSymbol[];
  loading: boolean;
  /** Por que a lista não veio (corretora fora do ar, slot sem configurar). */
  unavailable: string | null;
  autoFocus?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  crypto: 'Cripto',
  forex: 'Forex',
  stock: 'Ações',
  synthetics: 'Sintéticos',
};
const CATEGORY_ORDER = ['stock', 'crypto', 'forex', 'synthetics'];
const categoryLabel = (category: string | null) => (category ? CATEGORY_LABELS[category] ?? category : 'Outros');
const rank = (category: string | null) => {
  const index = CATEGORY_ORDER.indexOf(category ?? '');
  return index === -1 ? CATEGORY_ORDER.length : index;
};

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * O símbolo da operação: busca nos ativos da corretora, agrupados por
 * categoria. Digitar um símbolo fora da lista continua valendo — a lista ajuda,
 * não prende.
 */
export function SymbolPicker({ inputId, value, onChange, symbols, loading, unavailable, autoFocus }: SymbolPickerProps) {
  const id = inputId;
  const listId = `${id}-lista`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const matches = useMemo(() => {
    const query = normalize(value.trim());
    const found = symbols.filter((item) => !query || normalize(item.symbol).includes(query) || normalize(item.name).includes(query));
    return [...found].sort((a, b) => rank(a.category) - rank(b.category) || a.symbol.localeCompare(b.symbol));
  }, [symbols, value]);

  const exact = symbols.find((item) => item.symbol === value.trim().toUpperCase());

  function pick(item: BrokerSymbol) {
    onChange(item.symbol);
    setOpen(false);
  }

  function move(next: number) {
    const bounded = Math.max(0, Math.min(matches.length - 1, next));
    setActive(bounded);
    listRef.current?.querySelector(`[data-index="${bounded}"]`)?.scrollIntoView({ block: 'nearest' });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      else move(active + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(active - 1);
    } else if (event.key === 'Enter' && open && matches[active]) {
      event.preventDefault();
      pick(matches[active]);
    } else if (event.key === 'Escape' && open) {
      // Fecha só a lista, não o modal.
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      setOpen(false);
    }
  }

  const showList = open && matches.length > 0;
  let lastCategory: string | null | undefined;

  return (
    <div className="lev-picker">
      <input
        id={id}
        className="inp lev-symbol-input"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList ? `${id}-op-${active}` : undefined}
        value={value}
        onChange={(event) => {
          onChange(event.target.value.toUpperCase());
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        placeholder={loading ? 'Carregando os ativos…' : 'Busque: APPLE, EURUSD, BTC…'}
        maxLength={30}
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        autoFocus={autoFocus}
      />
      {showList ? (
        <ul ref={listRef} id={listId} role="listbox" className="lev-picker-list" aria-label="Ativos da corretora">
          {matches.map((item, index) => {
            const header = item.category !== lastCategory ? categoryLabel(item.category) : null;
            lastCategory = item.category;
            return (
              <li key={item.symbol} role="presentation">
                {header ? <div className="lev-picker-group" aria-hidden>{header}</div> : null}
                <div
                  id={`${id}-op-${index}`}
                  role="option"
                  aria-selected={index === active}
                  data-index={index}
                  className="lev-picker-option"
                  // mousedown, não click: o blur do campo fecharia a lista antes.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    pick(item);
                  }}
                  onMouseEnter={() => setActive(index)}
                >
                  <span className="lev-picker-symbol">{item.symbol}</span>
                  <span className="lev-picker-name truncate">{item.name}</span>
                  {item.marketClosed ? <span className="chip chip-warning">fechado</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
      <span className="field-hint">
        {exact
          ? `${exact.name} · ${categoryLabel(exact.category)}${exact.otc ? ' · OTC' : ''}`
          : unavailable ??
            (value.trim() && !loading && symbols.length > 0 ? 'Fora da lista da corretora: confira o símbolo.' : `${symbols.length || ''} ativos da corretora`.trim())}
      </span>
    </div>
  );
}
